// Case files uploaded from an identity go through the normal case upload and
// extraction. The identity_id form field makes the pipeline attach the document.
// An audio file is processed by the server itself as soon as it lands: its upload
// says so, and it is never sent to /extract again — only its run is followed.
// The runs of a batch are followed by one loop, which stops when the page is left.
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { followRuns, runStatusReader } from '@/features/documents/follow-runs'
import { AUTO_PIPELINE_ID } from '@/features/documents/types'
import { processUploads, type UploadedDocument } from '@/features/documents/upload-processing'
import { api, apiClient } from '@/services/api'
import type { CaseDocumentUploadResponse } from '@/services/api/cases'

export interface AttachIdentityDocumentsState {
  /** Files are being sent. */
  uploading: boolean
  /** Every file landed; extraction is still running. */
  processing: boolean
  /** Files sent so far, and how many there are in total. */
  uploaded: number
  total: number
}

export interface UseAttachIdentityDocumentsResult extends AttachIdentityDocumentsState {
  /** True while anything is in flight — the button's disabled state. */
  busy: boolean
  addFiles: (files: File[]) => Promise<void>
}

const IDLE: AttachIdentityDocumentsState = {
  uploading: false,
  processing: false,
  uploaded: 0,
  total: 0,
}

/**
 * Upload any supported case file to one identity and refresh after extraction.
 * *onFinished* is called once the run is terminal so the page can re-read evidence.
 */
export function useAttachIdentityDocuments(
  caseId: string,
  identityId: string,
  onFinished: () => void
): UseAttachIdentityDocumentsResult {
  const { t } = useTranslation()
  const [state, setState] = useState<AttachIdentityDocumentsState>(IDLE)
  // A second click while the first batch is in flight would upload the same
  // files twice; the ref guards it without waiting for a re-render.
  const inFlight = useRef(false)

  // Aborted when the page is left: the runs go on, and nothing reads them from here any more.
  const left = useRef<AbortSignal | undefined>(undefined)
  useEffect(() => {
    const controller = new AbortController()
    left.current = controller.signal
    return () => controller.abort()
  }, [])

  const addFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0 || inFlight.current) return
      inFlight.current = true
      setState({ uploading: true, processing: false, uploaded: 0, total: files.length })

      const uploads: UploadedDocument[] = []
      let failed = 0
      for (const file of files) {
        const form = new FormData()
        form.append('file', file)
        form.append('identity_id', identityId)
        try {
          const response = await apiClient.post<Partial<CaseDocumentUploadResponse>>(
            `/v1/cases/${caseId}/documents/upload`,
            form
          )
          if (response?.document_id)
            uploads.push({ ...response, document_id: response.document_id })
          else failed++
        } catch (error) {
          console.error(`Upload failed for ${file.name}:`, error)
          failed++
        }
        setState(prev => ({ ...prev, uploaded: prev.uploaded + 1 }))
      }

      if (failed > 0) {
        toast.error(t('identity.detail.addPhoto.uploadFailed', { count: failed }))
      }
      if (uploads.length === 0) {
        inFlight.current = false
        setState(IDLE)
        return
      }

      setState(prev => ({ ...prev, uploading: false, processing: true }))
      const signal = left.current
      try {
        await processUploads(uploads, {
          extract: documentIds =>
            api.cases.extractDocuments(caseId, {
              document_ids: documentIds,
              pipeline: AUTO_PIPELINE_ID,
            }),
          // One read per turn for the whole batch, whatever the number of its runs: the list of
          // the case's runs. A failed extraction is terminal; a read that failed is not.
          waitForRuns: runIds =>
            followRuns(runIds, {
              signal,
              read: runStatusReader(
                {
                  list: () => api.cases.getExtractionRuns(caseId),
                  one: runId => api.cases.getExtractionRunDetail(caseId, runId),
                },
                error => console.error('Extraction run poll failed:', error)
              ),
            }),
        })
        toast.success(t('identity.detail.linkedDocuments.uploadDone', { count: uploads.length }))
      } catch (error) {
        if (!signal?.aborted) {
          console.error('Extraction failed to start:', error)
          toast.error(t('identity.detail.linkedDocuments.extractFailed'))
        }
      } finally {
        inFlight.current = false
        // The page was left while its runs were followed: there is nothing to re-read into.
        if (!signal?.aborted) {
          setState(IDLE)
          // Whatever the run did, re-read the documents and face references.
          onFinished()
        }
      }
    },
    [caseId, identityId, onFinished, t]
  )

  return { ...state, busy: state.uploading || state.processing, addFiles }
}
